import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Loader2 } from "lucide-react";
import { fmtHours } from "@/foundation/formatting";

export default function CedolinoPreviewDialog({ open, onOpenChange, companyId, year, month, onDownload }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tracciato, setTracciato] = useState("buffetti");
  const [tracciati, setTracciati] = useState([]);

  // Carica dinamicamente i tracciati disponibili (endpoint discovery)
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/reports/tracciati");
        setTracciati(data.tracciati || []);
        if (data.default && !tracciato) setTracciato(data.default);
      } catch (e) { /* fallback statico: buffetti */ }
    })();
    /* eslint-disable-next-line */
  }, [open]);

  useEffect(() => {
    if (!open || !companyId) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/reports/company/cedolino/preview", {
          params: { company_id: companyId, year, month, tracciato },
        });
        setData(data);
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [open, companyId, year, month, tracciato]);

  const nf = (n) => (n == null ? "—" : Number(n).toFixed(2).replace(".", ","));
  const activeMeta = tracciati.find((t) => t.id === tracciato);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="cedolino-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={18} /> Anteprima CSV Cedolino
          </DialogTitle>
          <DialogDescription>
            Controlla le voci raggruppate per dipendente. Se tutto corretto, procedi al download del file `.csv`.
          </DialogDescription>
        </DialogHeader>

        <div className="border-b border-slate-200 pb-3 mb-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs uppercase tracking-widest text-slate-600 font-semibold">Tracciato:</span>
            <div className="flex gap-2 flex-wrap" data-testid="tracciato-selector">
              {(tracciati.length > 0 ? tracciati : [
                { id: "buffetti", label: "Buffetti eBridge" },
                { id: "zucchetti", label: "Zucchetti Paghe Web" },
              ]).map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTracciato(t.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    tracciato === t.id
                      ? "bg-emerald-800 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`tracciato-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {activeMeta?.description && (
            <div className="text-[11px] text-slate-500 mt-1.5" data-testid="tracciato-description">
              {activeMeta.description}
              {activeMeta.separator && (
                <span className="ml-2 text-slate-400">
                  · Separatore <code className="bg-slate-100 px-1 rounded">{activeMeta.separator}</code>
                  {activeMeta.encoding && <> · Encoding {activeMeta.encoding}</>}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 size={20} className="mr-2 animate-spin" /> Calcolo teorico in corso…
            </div>
          ) : !data ? (
            <div className="text-center text-slate-500 py-16">Nessun dato</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3">
                  <div className="text-xs uppercase tracking-widest text-emerald-800">Azienda</div>
                  <div className="text-sm font-semibold text-slate-900">{data.company?.name}</div>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Dipendenti</div>
                  <div className="text-2xl font-mono font-bold text-slate-900">{data.total_employees}</div>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Righe totali</div>
                  <div className="text-2xl font-mono font-bold text-slate-900">{data.total_rows}</div>
                </div>
              </div>

              {data.employees.length === 0 ? (
                <div className="text-center text-slate-400 py-8">Nessuna voce per il periodo {String(month).padStart(2, "0")}/{year}</div>
              ) : (
                <div className="space-y-3">
                  {data.employees.map((emp, i) => (
                    <div key={i} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`cedolino-emp-${emp.matricola || i}`}>
                      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                        <div className="text-sm font-semibold text-slate-900">
                          {emp.cognome} {emp.nome}
                          {emp.matricola && <span className="ml-2 text-[11px] font-mono text-slate-500">· Matr. {emp.matricola}</span>}
                        </div>
                        {emp.codice_fiscale && <div className="text-[10px] font-mono text-slate-400">{emp.codice_fiscale}</div>}
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50/60">
                          <tr>
                            <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold w-20">Codice</th>
                            <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Voce</th>
                            <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold w-24">Ore</th>
                            <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold w-20">Giorni</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {emp.voci.map((v, j) => (
                            <tr key={j} className={v.codice_voce === "001" ? "bg-emerald-50/40" : ""}>
                              <td className="px-3 py-1.5 font-mono text-xs">{v.codice_voce}</td>
                              <td className="px-3 py-1.5 text-xs">
                                <b>{v.descrizione}</b>
                                {v.note && <div className="text-[10px] text-slate-500">{v.note}</div>}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono">{v.ore != null ? `${fmtHours(v.ore)} h` : "—"}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{v.giorni != null ? v.giorni : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={() => { onDownload(tracciato); onOpenChange(false); }}
                  disabled={loading || !data?.total_rows}
                  className="bg-emerald-800 hover:bg-emerald-700 text-white"
                  data-testid="cedolino-download-confirm-btn">
            <Download size={14} className="mr-1" /> Scarica CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
